import { useState } from 'react';
import { AgAuthMain } from './AgAuthMain';
import { createMsalInstance, loginRequest } from './authConfig';
import { IMsalContext, MsalProvider } from '@azure/msal-react';

interface props {
  clientId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  LoadingComponent?: any;
  onAuthenticated?: (
    context: IMsalContext,
    setHasAppAccess: (value: ((prevState: object) => object) | object) => void,
    setLoadingProgress: (value: ((prevState: number) => number) | number) => void
  ) => Promise<void>;
  ErrorComponent: any;
  additionalScopes: string[];
  appAccesses: any;

  children: any;
}

/**
 * Async utility that acquires an MSAL access token (with a forced-refresh retry) and returns the idToken claims, throwing if the user must consent to additional scopes.
 *
 * @param msContext - The MSAL context instance for token acquisition.
 * @param undefined - MSAL scopes request to pass to acquireTokenSilent; defaults to loginRequest if omitted.
 * @returns The id token claims object, or throws with a consent error if redirects are required twice.
 */
export const getClaimsAsync = async (msContext: IMsalContext, scopes = loginRequest) => {
  try {
    const result = await msContext.instance.acquireTokenSilent(scopes);
    return result.idTokenClaims;
  } catch (e) {
    try {
      const result = await msContext.instance.acquireTokenSilent({
        ...scopes,
        forceRefresh: true
      });
      return result.idTokenClaims;
    } catch (e) {
      await msContext.instance.acquireTokenRedirect(scopes);
      throw new Error('User needs to consent to additional scopes');
    }
  }
};
/**
 * Returns an object with three async functions (getAccessTokenAsync, getIdTokenAsync, getRolesAsync) that acquire MSAL tokens, supporting a forced-refresh retry and redirect fallback on silent failure.
 *
 * @param msContext - The MSAL context used by all three token functions.
 * @returns An object with getAccessTokenAsync, getIdTokenAsync, and getRolesAsync methods.
 */
export const getAuthTokens = (msContext: IMsalContext) => {
  const getAccessTokenAsync = async (silentRequest = loginRequest): Promise<string> => {
    try {
      const result = await msContext.instance.acquireTokenSilent({ ...silentRequest });
      return result.accessToken;
    } catch (e) {
      // debugger;
      try {
        const result = await msContext.instance.acquireTokenSilent({
          ...silentRequest,
          forceRefresh: true
        });
        return result.accessToken;
      } catch (e) {
        await msContext.instance.acquireTokenRedirect({ ...silentRequest });
        return '';
      }
    }
  };
  const getIdTokenAsync = async (silentRequest = loginRequest) => {
    try {
      const result = await msContext.instance.acquireTokenSilent({ ...silentRequest });
      return result.idToken;
    } catch (e) {
      try {
        const result = await msContext.instance.acquireTokenSilent({
          ...silentRequest,
          forceRefresh: true
        });
        return result.idToken;
      } catch (e) {
        await msContext.instance.acquireTokenRedirect({ ...silentRequest });
        return '';
      }
    }
  };

  const getRolesAsync = async (msContext: IMsalContext) => {
    try {
      const result = await msContext.instance.acquireTokenSilent({ ...loginRequest });
      return result.idTokenClaims;
    } catch (e) {
      try {
        const result = await msContext.instance.acquireTokenSilent({
          ...loginRequest,
          forceRefresh: true
        });
        return result.idTokenClaims;
      } catch (e) {
        await msContext.instance.acquireTokenRedirect({ ...loginRequest});
        return '';
      }
    }
  }
  return { getAccessTokenAsync, getIdTokenAsync, getRolesAsync };
};
/**
 * The public MSAL auth provider that wraps children in an MsalProvider and the AgAuthMain OIDC flow, accepting client ID, loading/error UI, an onAuthenticated callback, and additional MSAL scopes.
 *
 * @param clientId - Azure AD MSAL client ID for the application.
 * @param LoadingComponent - Loading UI component rendered during auth.
 * @param ErrorComponent - Error UI component rendered on auth failure.
 * @param onAuthenticated - Optional callback invoked after successful OIDC authentication.
 * @param additionalScopes - Additional MSAL scopes to request consent for.
 * @param appAccesses - App access state object passed to the auth context.
 * @param children - Child components protected by the auth provider.
 */
export const AgAuth = ({
  LoadingComponent,
  ErrorComponent,
  clientId,
  onAuthenticated,
  additionalScopes = [],
  appAccesses = {},
  children
}: props) => {
  const [instance] = useState(createMsalInstance(clientId));
  return (
    <MsalProvider instance={instance}>
      <AgAuthMain
        onAuthenticated={onAuthenticated}
        ErrorComponent={ErrorComponent}
        LoadingComponent={LoadingComponent}
        clientId={clientId}
        appAccesses={appAccesses}
        extraScopesToConsent={additionalScopes}
      >
        {children}
      </AgAuthMain>
    </MsalProvider>
  );
};
export { useAgAuthContext } from './authHooks/useAgAuthContext';
export { useAgUsername } from './authHooks/useAgUsername';
export * from './authConfig';
export * from './AgAuthMain';
